import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useGetApiV1ClassroomdisciplineGradeAndFrequenceClassStudentsClassstudentid } from '@/shared/api/generated';

import { gradeConfigStateAtom } from '../store/atoms/gradeConfigAtom';
import { adaptGradeAndFrequence } from '../utils/adaptGradeAndFrequence';

/**
 * Fronteira de dados da feature — único ponto que conhece a origem da configuração.
 *
 * Busca a `GradeAndFrequenceViewModel` da turma selecionada (`selectedClassId` = `classStudentId`)
 * via hook gerado pelo Kubb e a adapta para o modelo de leitura da tela, semeando a working
 * copy editável do estado. O TanStack Query é o dono do fetch/cache; o átomo guarda apenas a
 * cópia editável (campos UI-only), semeada uma vez por turma carregada — padrão de formulário
 * inicializado a partir de dados do servidor.
 *
 * @returns `{ isLoading, isError, refetch }` — estado da carga da turma selecionada.
 */
export function useGradeConfigData() {
  const [state, setState] = useAtom(gradeConfigStateAtom);
  const { selectedClassId, selectedClassName } = state;
  const hasSelection = selectedClassId !== '';
  const seededFor = useRef<string | null>(null);

  const { data, isLoading, isError, refetch } =
    useGetApiV1ClassroomdisciplineGradeAndFrequenceClassStudentsClassstudentid(
      Number(selectedClassId),
      {
        query: { enabled: hasSelection },
      },
    );

  useEffect(() => {
    if (!hasSelection) {
      seededFor.current = null;
      if (state.classGroups.length > 0) setState(prev => ({ ...prev, classGroups: [] }));
      return;
    }
    if (data && seededFor.current !== selectedClassId) {
      seededFor.current = selectedClassId;
      const classGroup = adaptGradeAndFrequence(data, {
        id: selectedClassId,
        name: selectedClassName,
      });
      setState(prev => ({ ...prev, classGroups: [classGroup] }));
    }
  }, [hasSelection, data, selectedClassId, selectedClassName, state.classGroups.length, setState]);

  return {
    isLoading: hasSelection && isLoading,
    isError: hasSelection && isError,
    refetch,
  } as const;
}
